import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Link2, ListMusic, Music2 } from 'lucide-react'
import { useState } from 'react'

import { api } from '@/api/client'
import { Button, Sheet, Skeleton } from '@/components/ui'
import { Callout } from '@/features/settings/Callout'
import { SettingsSection } from '@/features/settings/SettingsSection'

import { ShareControls } from './ShareSheet'
import type { OwnerShare } from './types'

function shareStatus(share: OwnerShare) {
  if (share.revoked) return 'Stopped'
  if (!share.expires_at) return 'Until revoked'
  const expiry = new Date(share.expires_at)
  const date = expiry.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  return `${expiry.getTime() <= Date.now() ? 'Expired' : 'Expires'} ${date}`
}

export function SharesSection() {
  const query = useQuery({ queryKey: ['shares'], queryFn: () => api.get<OwnerShare[]>('/shares') })
  const [selected, setSelected] = useState<OwnerShare | null>(null)

  return (
    <>
      <SettingsSection
        icon={Link2}
        title="Shared links"
        description="Manage access to the songs and playlists you share."
      >
        {query.isPending ? (
          <div role="status" aria-label="Loading shared links" className="flex items-center gap-3 py-2">
            <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ) : query.isError ? (
          <Callout tone="danger" title="Could not load shared links">
            <Button variant="secondary" size="sm" className="mt-2" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </Callout>
        ) : query.data.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface-sunken p-4 text-sm">
            <p className="font-medium text-text">No shared links yet</p>
            <p className="mt-1 text-text-muted">Share a song or playlist from its menu. Your links will appear here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface-sunken">
            {query.data.map((share) => {
              const Icon = share.kind === 'playlist' ? ListMusic : Music2
              return (
                <li key={share.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(share)}
                    className="flex min-h-16 w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-hover"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-hover text-text-muted">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-text">{share.title}</span>
                      <span className="mt-0.5 block text-xs text-text-muted">
                        {share.kind === 'playlist' ? 'Playlist' : 'Song'} · {shareStatus(share)}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-text-subtle" aria-hidden="true" />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </SettingsSection>
      <Sheet
        open={!!selected}
        onOpenChange={(open) => { if (!open) setSelected(null) }}
        title="Manage shared link"
        description="Manage access and link lifetime."
      >
        {selected && <ShareControls key={selected.id} initial={selected} onDismiss={() => setSelected(null)} />}
      </Sheet>
    </>
  )
}
